import { Button, Card, Css, FormSectionLayout, Tooltip, useComputed, useModal } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { ReactNode, useCallback } from "react";
import { useRouteParams } from "src/hooks/useRouteParams";
import { SelectProductModal } from "src/routes/projects/change-events/guided/workflows/swapProductCatalog/components/SelectProductModal";
import { ChooseReplacementModal } from "src/routes/projects/change-events/guided/workflows/swapProductCatalog/inCatalog/components/ChooseReplacementModal";
import { SwapProductInCatalogFormInput } from "src/routes/projects/change-events/guided/workflows/swapProductCatalog/inCatalog/swapProductInCatalogWorkflow";
import { ProjectParams } from "src/routes/routesDef";
import { fail, queryResult } from "src/utils";
import { PRODUCT_FALLBACK_IMG_URL } from "src/utils/constants";
import {
  useSelectProductToReplaceMaterialVariantQuery,
  useSelectProductToReplaceProjectItemQuery,
} from "./1.SelectProductToReplaceStep.generated";
import { SwapProductNotInCatalogFormInput } from "./notInCatalog/swapProductNotInCatalogWorkflow";

type SelectProductToReplaceStepProps = {
  formState: ObjectState<SwapProductInCatalogFormInput> | ObjectState<SwapProductNotInCatalogFormInput>;
};

export function SelectProductToReplaceStep(props: SelectProductToReplaceStepProps) {
  const { formState } = props;
  const { projectId } = useRouteParams<ProjectParams>();
  const { openModal } = useModal();

  const { excludedProjectItemIds, swaps, canAddAnother } = useComputed(
    () => ({
      swaps: formState.swaps.rows,
      // A product can only be swapped once, so hide the originals staged in the *other* swaps.
      excludedProjectItemIds: formState.swaps.rows.map((r) => r.originalProjectItemId.value).compact(),
      // Only offer another swap once every staged one is filled in, so we never stack up empty blocks.
      canAddAnother: formState.swaps.valid,
    }),
    [formState],
  );

  // Picking a product here stages it as a brand new swap, whether it's the first one or another one.
  const addSwapModal = (
    <SelectProductModal
      projectId={projectId}
      onSelect={(selectedProductId) => formState.swaps.add({ originalProjectItemId: selectedProductId })}
      excludedProjectItemIds={excludedProjectItemIds}
    />
  );

  return (
    <FormSectionLayout
      title="Select the Product to Replace"
      description="Select the products on this project that you want to replace."
      // Once at least one swap is staged, adding another lives in the title row.
      actions={
        swaps.nonEmpty && canAddAnother
          ? [
              {
                label: "Add Another Swap",
                variant: "secondary",
                onClick: () => openModal({ size: "xxl", content: addSwapModal }),
              },
            ]
          : undefined
      }
      // With nothing staged yet the empty state is the whole step, so it sits above the (empty) section list.
      initialFields={
        swaps.isEmpty ? <AddProductReplacement label="Select Product" modalContent={addSwapModal} /> : undefined
      }
      sections={swaps.map((swap, i) => ({
        title: `Product Swap ${i + 1}`,
        // A swap is always needed, so the last one staged can't be dropped.
        actions: swaps.nonEmpty
          ? [{ kind: "icon", icon: "trash", onClick: () => formState.swaps.remove(swap.value) }]
          : undefined,
        fields: (
          <ProductSwapFields
            key={`${swap.originalProjectItemId.value}-${swap.proposedMaterialVariantId.value}`}
            projectId={projectId}
            swap={swap}
            excludedProjectItemIds={excludedProjectItemIds}
          />
        ),
      }))}
    />
  );
}

type ProductSwapFieldsProps = {
  projectId: string;
  swap: ObjectState<SwapProductInCatalogFormInput["swaps"][number]>;
  /** Originals staged in the other swaps, which can't be picked again here. */
  excludedProjectItemIds: string[];
};

function ProductSwapFields(props: ProductSwapFieldsProps) {
  const { projectId, swap, excludedProjectItemIds } = props;
  const { originalProductId, proposedMaterialVariantId } = useComputed(
    () => ({
      // This section is rendered when there is at least one swap staged, so the original product id is always set.
      originalProductId: swap.originalProjectItemId.value ?? fail(),
      proposedMaterialVariantId: swap.proposedMaterialVariantId.value,
    }),
    [swap],
  );

  const onSelectOriginal = useCallback(
    (selectedProductId: string) => {
      // A different product likely has different placeholder requirements, so drop any staged replacement
      if (selectedProductId !== swap.originalProjectItemId.value) {
        swap.set({ id: undefined, proposedMaterialVariantId: undefined });
      }
      swap.originalProjectItemId.set(selectedProductId);
    },
    [swap],
  );

  const onSelectReplacement = useCallback(
    (selectedMaterialVariantId: string) => {
      swap.proposedMaterialVariantId.set(selectedMaterialVariantId);
    },
    [swap],
  );

  return (
    <div css={Css.df.gap2.$}>
      {/* Show the original product card. */}
      <ViewOriginalProduct
        projectId={projectId}
        originalProjectItemId={originalProductId}
        excludedProjectItemIds={excludedProjectItemIds}
        onChange={onSelectOriginal}
      />

      {/* Show 'Choose Replacement' when no replacement product is selected. Otherwise just show the product card. */}
      {!proposedMaterialVariantId ? (
        <AddProductReplacement
          label="Choose Replacement"
          modalContent={<ChooseReplacementModal projectItemId={originalProductId} onSelect={onSelectReplacement} />}
        />
      ) : (
        <ViewReplacementProduct
          originalProjectItemId={originalProductId}
          proposedMaterialVariantId={proposedMaterialVariantId}
          onChange={onSelectReplacement}
        />
      )}
    </div>
  );
}

type ViewOriginalProductProps = {
  projectId: string;
  /** The chosen product's PI. When set, its card takes the place of the empty state. */
  originalProjectItemId: string;
  /** Originals staged in the other swaps, which can't be picked again here. */
  excludedProjectItemIds: string[];
  /** The function to call when the selected product is changed. */
  onChange: (selectedProductId: string) => void;
};
function ViewOriginalProduct(props: ViewOriginalProductProps) {
  const { projectId, originalProjectItemId, excludedProjectItemIds, onChange } = props;
  const { openModal } = useModal();
  const query = useSelectProductToReplaceProjectItemQuery({ variables: { projectItemId: originalProjectItemId } });
  return queryResult(query, ({ projectItem }) => (
    <ProductPanel
      title="Original"
      onClick={() =>
        openModal({
          size: "xxl",
          content: (
            <SelectProductModal
              projectId={projectId}
              onSelect={onChange}
              initialSelectedProductId={originalProjectItemId}
              excludedProjectItemIds={excludedProjectItemIds}
            />
          ),
        })
      }
    >
      <Card
        imgSrc={projectItem.materialVariant?.featuredImage?.asset?.previewUrl ?? PRODUCT_FALLBACK_IMG_URL}
        title={projectItem.materialVariant?.displayName ?? ""}
        subtitle={projectItem.materialVariant?.listing.brand?.name ?? projectItem.materialVariant?.listing.name ?? ""}
        type="list"
        fullWidth
      />
    </ProductPanel>
  ));
}

type ViewReplacementProductProps = {
  /** The original PI, which drives the replacement search when re-opening the modal. */
  originalProjectItemId: string;
  /** The chosen replacement's MV. When set, its card takes the place of the empty state. */
  proposedMaterialVariantId: string;
  /** The function to call when the selected replacement is changed. */
  onChange: (selectedMaterialVariantId: string) => void;
};
function ViewReplacementProduct(props: ViewReplacementProductProps) {
  const { originalProjectItemId, proposedMaterialVariantId, onChange } = props;
  const { openModal } = useModal();
  const query = useSelectProductToReplaceMaterialVariantQuery({
    variables: { materialVariantId: proposedMaterialVariantId },
  });
  return queryResult(query, ({ materialVariant }) => (
    <ProductPanel
      title="Replacement"
      onClick={() =>
        openModal({
          size: "xxl",
          content: <ChooseReplacementModal projectItemId={originalProjectItemId} onSelect={onChange} />,
        })
      }
    >
      <Card
        imgSrc={materialVariant.featuredImage?.asset?.previewUrl ?? PRODUCT_FALLBACK_IMG_URL}
        title={materialVariant.displayName ?? ""}
        subtitle={materialVariant.listing.brand?.name ?? materialVariant.listing.name ?? ""}
        fullWidth
        type="list"
      />
    </ProductPanel>
  ));
}

/** The shared panel chrome, i.e. the "Original"/"Replacement" title above the clickable card. */
function ProductPanel(props: { title: string; onClick: () => void; children: ReactNode }) {
  const { title, onClick, children } = props;
  return (
    <div css={Css.df.fdc.gap1.fb0.fg1.$} onClick={onClick}>
      <span css={Css.smSb.$}>{title}</span>
      <Tooltip title="Click to select a different product">{children}</Tooltip>
    </div>
  );
}

type AddProductReplacementProps = {
  /** The button label, i.e. "Select Product" or "Choose Replacement". */
  label: string;
  /** The selection modal to open, i.e. `SelectProductModal` or `ChooseReplacementModal`. */
  modalContent: ReactNode;
};
function AddProductReplacement(props: AddProductReplacementProps) {
  const { label, modalContent } = props;
  const { openModal } = useModal();
  return (
    <div css={Css.df.fdc.gap1.fb0.fg1.$}>
      <div css={Css.df.aic.jcc.hPx(178).$}>
        <Button
          label={label}
          variant="textSecondary"
          onClick={() => openModal({ size: "xxl", content: modalContent })}
        />
      </div>
    </div>
  );
}
